import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle, XCircle, RefreshCw, Loader2, AlertTriangle, MapPin, User, Building, PenLine } from 'lucide-react'
import api from '@/utils/api'

interface DefectInfo {
  propertyAddress?: string
  tenantName?: string
  projectName?: string
  description?: string
  completionPdfPath?: string
  completionNotes?: string
  signaturePath?: string
}

export default function SignaturePage() {
  const { token } = useParams<{ token: string }>()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [defect, setDefect] = useState<DefectInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [lastPoint, setLastPoint] = useState<{ x: number; y: number } | null>(null)
  const [showSignatureOverlay, setShowSignatureOverlay] = useState(false)

  useEffect(() => { loadDefect() }, [token])

  useEffect(() => {
    if (showSignatureOverlay && canvasRef.current) {
      setTimeout(() => setupCanvas(), 50)
    }
  }, [showSignatureOverlay])

  useEffect(() => {
    if (showSignatureOverlay) {
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.height = '100%'
    } else {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.height = ''
    }
    return () => { document.body.style.overflow = ''; document.body.style.position = ''; document.body.style.width = ''; document.body.style.height = '' }
  }, [showSignatureOverlay])

  const loadDefect = async () => {
    try {
      const response = await api.get(`/defects/by-token/${token}`)
      setDefect(response.data)
      if (response.data.signaturePath) setSubmitted(true)
    } catch (err: any) {
      if (err.response?.status === 404) setError('Der Link ist ungueltig oder abgelaufen.')
      else if (err.response?.status === 410) { setError('Bereits unterschrieben.'); setSubmitted(true) }
      else setError('Ein Fehler ist aufgetreten.')
    } finally { setLoading(false) }
  }

  const setupCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!
    canvas.width = window.innerWidth - 32
    canvas.height = window.innerHeight - 220
    ctx.strokeStyle = '#000'
    ctx.lineWidth = 3
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }

  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    if ('touches' in e) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY }
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY }
  }

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault(); e.stopPropagation()
    setIsDrawing(true); setLastPoint(getCoordinates(e)); setHasSignature(true)
  }
  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || !lastPoint) return
    e.preventDefault(); e.stopPropagation()
    const ctx = canvasRef.current!.getContext('2d')!
    const coords = getCoordinates(e)
    ctx.beginPath(); ctx.moveTo(lastPoint.x, lastPoint.y); ctx.lineTo(coords.x, coords.y); ctx.stroke()
    setLastPoint(coords)
  }
  const stopDrawing = (e?: React.MouseEvent | React.TouchEvent) => {
    if (e) { e.preventDefault(); e.stopPropagation() }
    setIsDrawing(false); setLastPoint(null)
  }
  const clearCanvas = () => {
    const ctx = canvasRef.current!.getContext('2d')!
    ctx.clearRect(0, 0, canvasRef.current!.width, canvasRef.current!.height)
    setHasSignature(false)
  }

  const handleSubmit = async () => {
    if (!hasSignature) return
    setSubmitting(true)
    try {
      await api.post(`/defects/by-token/${token}/signature`, { signature: canvasRef.current!.toDataURL('image/png') })
      setShowSignatureOverlay(false); setSubmitted(true)
    } catch (err: any) { setError(err.message || 'Fehler') }
    finally { setSubmitting(false) }
  }

  const handleRefuse = async () => {
    const reason = prompt('Bitte Grund angeben:')
    if (reason === null) return
    setSubmitting(true)
    try {
      await api.post(`/defects/by-token/${token}/refuse`, { reason: reason || 'Keine Angabe' })
      setSubmitted(true); setError('Verweigerung gespeichert.')
    } catch (err: any) { setError(err.message || 'Fehler') }
    finally { setSubmitting(false) }
  }

  if (loading) return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="text-center"><Loader2 className="mx-auto h-12 w-12 animate-spin text-blue-500" /><p className="mt-4 text-gray-600">Wird geladen...</p></div>
    </div>
  )

  if (submitted) return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-lg">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100"><CheckCircle className="h-8 w-8 text-green-600" /></div>
        <h1 className="mb-2 text-2xl font-bold text-gray-900">Vielen Dank!</h1>
        <p className="text-gray-600">{error || 'Ihre Unterschrift wurde erfolgreich gespeichert.'}</p>
        <p className="mt-4 text-sm text-gray-500">Sie koennen dieses Fenster nun schliessen.</p>
      </div>
    </div>
  )

  if (error) return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-lg">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100"><AlertTriangle className="h-8 w-8 text-red-600" /></div>
        <h1 className="mb-2 text-2xl font-bold text-gray-900">Fehler</h1>
        <p className="text-gray-600">{error}</p>
      </div>
    </div>
  )

  if (showSignatureOverlay) return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white" style={{ touchAction: 'none' }}>
      <div className="flex items-center justify-between bg-blue-600 px-4 py-3 text-white">
        <h1 className="text-lg font-bold">Unterschrift</h1>
        <button onClick={() => setShowSignatureOverlay(false)} className="rounded-lg p-2 hover:bg-blue-700"><XCircle className="h-6 w-6" /></button>
      </div>
      <div className="flex flex-1 flex-col p-4" onTouchMove={(e) => e.preventDefault()}>
        <p className="mb-2 text-center text-sm text-gray-600">Unterschreiben Sie mit dem Finger</p>
        <div className="flex-1 overflow-hidden rounded-lg border-2 border-gray-300 bg-white" style={{ touchAction: 'none' }}>
          <canvas ref={canvasRef} className="h-full w-full" style={{ touchAction: 'none' }}
            onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
            onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} />
        </div>
        <div className="mt-4 flex gap-3">
          <button onClick={clearCanvas} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gray-100 px-4 py-3 text-gray-700"><RefreshCw className="h-5 w-5" />Loeschen</button>
          <button onClick={handleSubmit} disabled={!hasSignature || submitting} className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold ${hasSignature && !submitting ? 'bg-green-600 text-white' : 'cursor-not-allowed bg-gray-200 text-gray-400'}`}>
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle className="h-5 w-5" />}Bestaetigen
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-blue-600 px-4 py-6 text-white"><div className="mx-auto max-w-lg"><h1 className="text-xl font-bold">Maengelbeseitigung</h1><p className="mt-1 text-sm text-blue-100">Bestaetigung der Fertigstellung</p></div></div>
      <div className="mx-auto max-w-lg space-y-4 p-4">
        {defect && (
          <div className="rounded-xl bg-white p-4 shadow-sm">
            <h2 className="mb-3 font-semibold text-gray-900">Informationen zum Mangel</h2>
            <div className="space-y-3">
              {defect.propertyAddress && <div className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" /><div><p className="text-sm text-gray-500">Adresse</p><p className="text-gray-900">{defect.propertyAddress}</p></div></div>}
              {defect.tenantName && <div className="flex items-start gap-3"><User className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" /><div><p className="text-sm text-gray-500">Mieter</p><p className="text-gray-900">{defect.tenantName}</p></div></div>}
              {defect.projectName && <div className="flex items-start gap-3"><Building className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" /><div><p className="text-sm text-gray-500">Projekt</p><p className="text-gray-900">{defect.projectName}</p></div></div>}
              {defect.description && <div className="border-t pt-3"><p className="mb-1 text-sm text-gray-500">Beschreibung</p><p className="text-gray-700">{defect.description}</p></div>}
            </div>
          </div>
        )}
        {defect?.completionPdfPath && <div className="rounded-xl bg-white p-4 shadow-sm"><h3 className="mb-3 font-semibold text-gray-900">Mangelbericht</h3><embed src={`/api/uploads/defects/${defect.completionPdfPath}`} type="application/pdf" width="100%" height="500px" className="rounded-lg border" /></div>}
        {!defect?.completionPdfPath && defect?.completionNotes && <div className="rounded-xl bg-green-50 p-4"><h3 className="mb-2 font-medium text-green-900">Durchgefuehrte Arbeiten</h3><p className="text-green-800">{defect.completionNotes}</p></div>}
        <button onClick={() => setShowSignatureOverlay(true)} className="flex w-full items-center justify-center gap-3 rounded-xl bg-blue-600 px-6 py-5 text-lg font-semibold text-white shadow-md transition-colors hover:bg-blue-700"><PenLine className="h-6 w-6" />Jetzt unterschreiben</button>
        <button onClick={handleRefuse} disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-red-200 px-6 py-3 font-medium text-red-600 transition-colors hover:bg-red-50"><XCircle className="h-5 w-5" />Unterschrift verweigern</button>
        <p className="px-4 pb-4 text-center text-xs text-gray-400">Mit Ihrer Unterschrift bestaetigen Sie die Maengelbeseitigung.</p>
      </div>
    </div>
  )
}
